import { StrictMode, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { PersistQueryClientProvider, removeOldestQuery } from '@tanstack/react-query-persist-client'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import { BrowserRouter } from 'react-router-dom'
import './index.css'
import App from './App'
import { AuthProvider } from './lib/auth'
import { createApiClient, TRPCProvider } from './lib/trpc'
import { ToastProvider } from './components/toast'

/** procedures whose answers never outlive the session (path segments of their query keys) */
const NEVER_PERSISTED = new Set(['search', 'networks', 'openPoolDeposit'])
const DAY_MS = 24 * 60 * 60_000

/**
 * The query cache outlives the tab (Nov 4 2026, John: "refreshing/reloading feels a bit slow"): the last answers are
 * kept in localStorage, so a reload paints the dashboard from them at once and the refetches run behind it instead
 * of in front of a spinner. A new build starts empty (the id is the key), a copy older than a day is dropped, and a
 * full store sheds its oldest query. Nothing here without storage (a private window): the cache then lives in memory.
 */
function createPersister() {
  try {
    const storage = window.localStorage
    storage.setItem('dispence.storage-check', '1')
    storage.removeItem('dispence.storage-check')
    return createSyncStoragePersister({ storage, key: 'dispence.queries', throttleTime: 1_000, retry: removeOldestQuery })
  } catch {
    return null
  }
}

function Root() {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        // answers stay good for ten seconds between screens; a persisted copy is kept for a day
        defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 1, staleTime: 10_000, gcTime: DAY_MS } },
      }),
  )
  const [apiClient] = useState(() => createApiClient())
  const [persister] = useState(createPersister)
  const tree = (
    <TRPCProvider trpcClient={apiClient} queryClient={queryClient}>
      <AuthProvider>
        <ToastProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </ToastProvider>
      </AuthProvider>
    </TRPCProvider>
  )
  if (!persister) return <QueryClientProvider client={queryClient}>{tree}</QueryClientProvider>
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: DAY_MS,
        buster: __BUILD_ID__,
        // settled answers only; a token search is typed again rather than kept
        dehydrateOptions: {
          // token searches, the exchanges' rails and the waiting deposit address are read live each session: a rail answer
          // that came back empty once would otherwise grey an exchange out, or hide it, until the next read (Nov 5 2026)
          shouldDehydrateQuery: (query) => query.state.status === 'success' && !(Array.isArray(query.queryKey[0]) && (query.queryKey[0] as string[]).some((part) => NEVER_PERSISTED.has(part))),
        },
      }}
    >
      {tree}
    </PersistQueryClientProvider>
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Root />
  </StrictMode>,
)
